import React, { useRef, useState, useCallback } from "react";
import { observer } from "mobx-react";
import {
  CloudUpload,
  Description,
  Close,
  People,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useContactsMapping } from "../../hooks";
import {
  Container,
  DropZone,
  DropZoneIcon,
  DropZoneText,
  DropZoneHint,
  FileInfo,
  FileDetails,
  FileIcon,
  FileText,
  FileName,
  FileSize,
  RemoveButton,
  ContactCount,
  ErrorMessage,
  LoadingOverlay,
} from "./ContactsUpload.styled";

const ContactsUpload = observer(() => {
  const fileInputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const {
    fileUploaded,
    contactCount,
    isParsingFile,
    parseError,
    handleFileUpload,
    handleRemoveFile,
  } = useContactsMapping();

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = e.dataTransfer?.files;
      if (files?.length) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const files = e.target?.files;
      if (files?.length) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  const handleDropZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isParsingFile) {
    return (
      <Container>
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      </Container>
    );
  }

  return (
    <Container>
      {!fileUploaded ? (
        <DropZone
          $isDragActive={isDragActive}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleDropZoneClick}
          role="button"
          tabIndex={0}
          aria-label="Upload CSV file"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileInputChange}
            aria-hidden="true"
          />
          <DropZoneIcon>
            <CloudUpload />
          </DropZoneIcon>
          <DropZoneText>
            Drag and drop your CSV file here, or <span>browse</span>
          </DropZoneText>
          <DropZoneHint>
            Supports CSV files up to 100MB
          </DropZoneHint>
        </DropZone>
      ) : (
        <>
          <FileInfo>
            <FileDetails>
              <FileIcon>
                <Description />
              </FileIcon>
              <FileText>
                <FileName>{fileUploaded.name}</FileName>
                <FileSize>{formatFileSize(fileUploaded.size)}</FileSize>
              </FileText>
            </FileDetails>
            <RemoveButton
              onClick={handleRemoveFile}
              aria-label="Remove file"
              type="button"
            >
              <Close />
            </RemoveButton>
          </FileInfo>

          {contactCount > 0 && (
            <ContactCount>
              <People />
              {contactCount} contact{contactCount !== 1 ? "s" : ""} found
            </ContactCount>
          )}
        </>
      )}

      {parseError && (
        <ErrorMessage role="alert">
          <ErrorIcon />
          {parseError}
        </ErrorMessage>
      )}
    </Container>
  );
});

export default ContactsUpload;
